import { useEffect, useRef, useState } from 'react';
import { traderAPI, TraderCommissionStats } from '../lib/api';
import { useTraderTime } from '../contexts/TraderTimeContext';
import { traderDateKey, traderInputToUTC } from '../lib/traderTime';
import { TRADER_BALANCE_REFRESH_INTERVAL_MS } from '../lib/polling';
import { balanceEvents, BALANCE_EVENTS } from '../lib/events';

// Results belong to both an account timezone and a calendar day. Never relabel an
// old response as today's earnings after midnight or after a timezone change.
export function useTraderCommissionStats() {
    const { offsetMinutes } = useTraderTime();
    const [snapshot, setSnapshot] = useState<{ key: string; stats: TraderCommissionStats } | null>(null);
    const [, tick] = useState(0);
    const key = `${offsetMinutes}:${traderDateKey(Date.now(), offsetMinutes)}`;
    const currentOffset = useRef(offsetMinutes);
    currentOffset.current = offsetMinutes;

    useEffect(() => {
        let active = true;
        let version = 0;
        let midnight: ReturnType<typeof setTimeout>;
        const refresh = async () => {
            const request = ++version;
            const day = traderDateKey(Date.now(), offsetMinutes);
            const requestKey = `${offsetMinutes}:${day}`;
            tick(value => value + 1);
            clearTimeout(midnight);
            const nextDay = traderInputToUTC(day, offsetMinutes).getTime() + 86400000;
            midnight = setTimeout(() => { void refresh(); }, Math.max(1, nextDay - Date.now()));
            try {
                const stats = await traderAPI.getCommissionStats();
                if (!active || request !== version || currentOffset.current !== offsetMinutes
                    || traderDateKey(Date.now(), offsetMinutes) !== day) return;
                setSnapshot(stats ? { key: requestKey, stats } : null);
            } catch {
                if (active && request === version) setSnapshot(null);
            }
        };
        const onRefresh = () => { void refresh(); };
        const onVisible = () => { if (document.visibilityState === 'visible') onRefresh(); };
        onRefresh();
        const interval = setInterval(onRefresh, TRADER_BALANCE_REFRESH_INTERVAL_MS);
        window.addEventListener('focus', onRefresh);
        document.addEventListener('visibilitychange', onVisible);
        const events = Object.values(BALANCE_EVENTS);
        events.forEach(event => balanceEvents.on(event, onRefresh));
        return () => {
            active = false;
            clearInterval(interval);
            clearTimeout(midnight);
            window.removeEventListener('focus', onRefresh);
            document.removeEventListener('visibilitychange', onVisible);
            events.forEach(event => balanceEvents.off(event, onRefresh));
        };
    }, [offsetMinutes]);

    return snapshot?.key === key ? snapshot.stats : null;
}
